import React, { useEffect, useState } from 'react';
import { Alert, Button } from '@roger/ui-components';
import { AlertTriangle, LockOpen } from 'lucide-react';
import { getVisitSchemaRogerdocV2VisitsVisitIdSchemaGet } from '../../services/api/sdk.gen';
import {
  describeChartFixTarget,
  type ChartFixTarget,
} from '../oasis/cmsFixTarget';
import type { OASISSchema } from '../oasis/types';

interface LockedChartFixBannerProps {
  visitId: string;
  target: ChartFixTarget;
  /** False: no button, and the banner says who can reopen it. */
  canReopen: boolean;
  reopening: boolean;
  onReopen: () => void;
}

/**
 * A CMS fix link that lands on a locked chart: names what CMS will reject and
 * offers Reopen, which the panel confirms before the form opens there.
 */
export const LockedChartFixBanner: React.FC<LockedChartFixBannerProps> = ({
  visitId,
  target,
  canReopen,
  reopening,
  onReopen,
}) => {
  const [schema, setSchema] = useState<OASISSchema | null>(null);
  useEffect(() => {
    let cancelled = false;
    getVisitSchemaRogerdocV2VisitsVisitIdSchemaGet({
      path: { visit_id: visitId },
    })
      .then(response => {
        const data = response?.data as
          { schema_data?: OASISSchema } | undefined;
        if (!cancelled && data?.schema_data) setSchema(data.schema_data);
      })
      // The item code still names it; the question text is a nicety.
      .catch(err => console.error('Failed to load the chart questions:', err));
    return () => {
      cancelled = true;
    };
  }, [visitId]);

  const label = describeChartFixTarget(schema, target);
  return (
    <Alert
      variant='destructive'
      className='flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between'
    >
      {/* Wrapped so Alert's absolute icon styling doesn't apply. */}
      <div className='flex items-start gap-2 text-sm'>
        <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
        <div>
          <p className='font-medium'>
            {label
              ? `CMS will reject this chart. To fix: ${label}.`
              : 'CMS will reject an answer in this chart.'}
          </p>
          <p>
            {canReopen
              ? 'The chart is locked. Reopen it to fix this.'
              : 'Ask the assigned clinician or someone who can reopen charts.'}
          </p>
        </div>
      </div>
      {canReopen && (
        <Button
          size='sm'
          className='flex shrink-0 items-center gap-1.5 self-start sm:self-auto'
          onClick={onReopen}
          disabled={reopening}
        >
          <LockOpen className='h-4 w-4' />
          Reopen and fix
        </Button>
      )}
    </Alert>
  );
};
